import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const login = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const logout = async () => {
  try {
    const response = await api.post('/auth/logout');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('adminToken'); // cleanup legacy key
    localStorage.removeItem('admin_user');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const sendOTP = async (email, purpose = 'login') => {
  try {
    const response = await axios.post(`${API_BASE}/auth/otp/send`, { email, purpose });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const verifyOTP = async (email, code) => {
  try {
    const response = await axios.post(`${API_BASE}/auth/otp/verify`, { email, code });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Homepage API
export const getHomepageSection = async (section) => {
  try {
    // Use homepage-ml endpoint which has full multilingual data (_pt/_en fields)
    const response = await axios.get(`${API_BASE}/homepage-ml/${section}?lang=raw`);
    return { success: true, data: response.data };
  } catch (error) {
    // Fallback to legacy endpoint
    try {
      const fallback = await axios.get(`${API_BASE}/homepage/${section}`);
      return { success: true, data: fallback.data };
    } catch (e) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }
};

export const updateHomepageSection = async (section, data) => {
  try {
    // Use homepage-ml endpoint which accepts full multilingual dict
    const response = await axios.put(`${API_BASE}/homepage-ml/${section}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const getHighlight = async (type) => {
  try {
    const response = await axios.get(`${API_BASE}/homepage/highlight/${type}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const updateHighlight = async (type, data) => {
  try {
    const response = await axios.put(`${API_BASE}/homepage/highlight/${type}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// CRM API
export const getCRMClients = async () => {
  try {
    const response = await axios.get(`${API_BASE}/crm/clients`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const getCRMDashboard = async () => {
  try {
    const response = await axios.get(`${API_BASE}/crm/dashboard/stats`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Procurement API
export const getProcurementDashboard = async () => {
  try {
    const response = await axios.get(`${API_BASE}/procurement/dashboard/stats`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Trading API
export const getTradingDashboard = async () => {
  try {
    const response = await axios.get(`${API_BASE}/trading/dashboard/stats`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Image Upload (placeholder - implement multipart upload)
export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Translation API
export const translateText = async (text, sourceLang = 'pt', targetLang = 'en') => {
  try {
    const response = await axios.post(`${API_BASE}/translate/`, {
      text,
      source_lang: sourceLang,
      target_lang: targetLang
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

// Multilingual Homepage API
export const getHomepageSectionML = async (section, lang = 'pt') => {
  try {
    const response = await axios.get(`${API_BASE}/homepage-ml/${section}`, {
      params: { lang }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAllHomepageSectionsML = async (lang = 'pt') => {
  try {
    const response = await axios.get(`${API_BASE}/homepage-ml/`, {
      params: { lang }
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
